import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ListItemButton, Stack, Typography, CircularProgress } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import InventoryItemView from './InventoryItemView';
import BundleItemQuantityForm from '../forms/BundleItemQuantityForm';
import { useDeleteBundleItem } from '../../../hooks/inventory/useDeleteBundleItem';
import { useCreateBundleItem } from '../../../hooks/inventory/useCreateBundleItem';
import InventoryItemSearch from '../../history/modals/InventoryItemSearch';

export default function BundleItems({loading, bundle = null, open, onClose, onAddItemSuccess, onSaveQuantitySuccess, onRemoveItemSuccess, mode = 'edit'}) {
    const { t } = useTranslation('common'); 

    const handleClose = () => {
        onClose();
    };

    const { remove } = useDeleteBundleItem(onRemoveItemSuccess);
    const { create } = useCreateBundleItem(onAddItemSuccess);

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [itemSearchOpen, setItemSearchOpen] = React.useState(false);

    const handleListItemClick = (item) => {
        setViewOpen(true);
        setSelectedItem(item.inventoryItem);
    };

    const { showSnackbar } = useSnackbar();

    const onAddItemButtonClick = () => {
        setSelectedItem(null);
        setItemSearchOpen(true);
    };

    const handleAddItem = async (item) => {
      const data = { 
        "bundle_id": bundle.id,
        "inventory_item_ids": item.id
      };

      try {
        await create(data);
      } catch(error) {
        if (error.status === 409)
        {
          showSnackbar(t("inventory.bundle_items.messages.conflict"), "error");
        }
        else {
          showSnackbar(t('inventory.bundle_items.messages.save_fail'), "error");
        }
      }
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setFormOpen(true);
    };

    const handleRemove = async (item) => {
        try {
            await remove(item);
            showSnackbar(t('inventory.bundle_items.messages.remove_success', { name: item.name }), "success");
        } catch (err) {
            showSnackbar(t('inventory.bundle_items.messages.remove_fail', { name: item.name }), "error");
        }
    };

    return (
        <React.Fragment>
        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
              <>
              <Typography variant="h6" sx={{margin: 0, lineHeight: 1}}>Bundle</Typography>
              <Typography variant="h5" sx={{margin: 0, lineHeight: 0}}>{bundle?.name}</Typography>
              </>
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                {loading ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      minHeight: 500 
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                <List sx={{ minHeight: 500 }}>
                    {bundle?.items.length === 0 ?
                    <ListItem sx={{ justifyContent: "center" }}>
                      <Typography component="span" variant="body2" color="text.primary">
                        no items
                      </Typography>
                    </ListItem>
                    : null }
                    {bundle?.items?.map((item) => (
                    <ListItem
                        key={item.id}
                        secondaryAction={
                        mode === 'edit' ?
                          <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                              <VerticalHamburgerMenu
                              options={[
                                  { 
                                      id: 1, 
                                      name: 'View',
                                      callback: () => handleListItemClick(item)
                                  },
                                  { 
                                      id: 2, 
                                      name: 'Edit Qty',
                                      callback: () => handleEdit(item)
                                  },
                                  {
                                      id: 4,
                                      name: 'Remove',
                                      callback: () => handleRemove(item),
                                      confirmMessage: t('inventory.bundle_items.messages.remove_confirm', { name: `${item.inventoryItem.name}` })
                                  },
                              ]}
                              />
                          </Box>
                        : null
                        }
                    >
                        <ListItemButton onClick={() => handleListItemClick(item)}>
                            <ListItemIcon>
                                <InventoryIcon fontSize='large' color='primary' />
                            </ListItemIcon>
                            <ListItemText
                            primary={`${item.inventoryItem.name}`}
                            secondary={ 
                            <>
                                <Typography component="span" variant="body2" color={Number(item.inventoryItem.current_stock) <= Number(item.inventoryItem.low_stock) ? "error" : "text.primary" } >
                                {`Qty: ${removeZeroDecimals(item.quantity)}${item.inventoryItem.uom}`}
                                </Typography>
                            </>
                            }
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>
                )}
                </>
            </DialogContent>
            <DialogActions>
              { mode === 'edit' ?
              <Button onClick={onAddItemButtonClick}>Add Item</Button>
              : null }
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        {selectedItem && formOpen ?
        <BundleItemQuantityForm 
          successCallback={onSaveQuantitySuccess}
          item={selectedItem}
          open={formOpen}
          onClose={() => setFormOpen(false)}
        />
        : null }
        {selectedItem && viewOpen ?
        <InventoryItemView 
            handleCreateTransaction={null}
            handleEdit={null}
            item={selectedItem}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        : null }
        <InventoryItemSearch 
          addButtonCallback={handleAddItem}
          open={itemSearchOpen}
          onClose={() => {setItemSearchOpen(false)}}
          mode='bundle'
        />
        
        </React.Fragment>
    );
}